<template>
  <div 
    ref="containerRef"
    class="secure-wallpaper-image"
    :class="containerClass"
    @contextmenu.prevent
    @dragstart.prevent
    @selectstart.prevent
  >
    <!-- Loading state -->
    <div 
      v-if="loading" 
      class="flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse"
      :class="aspectClass"
    >
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
    </div>

    <!-- Error state -->
    <div 
      v-else-if="error" 
      class="flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400"
      :class="aspectClass"
    >
      <div class="text-center p-4">
        <svg class="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
        <p class="text-sm">Failed to load</p>
      </div>
    </div>

    <!-- Secure media -->
    <video
      v-else-if="secureImageUrl && isVideoUrl"
      ref="videoRef"
      :src="secureImageUrl"
      :alt="alt"
      :class="[imageClass, cursorClass]"
      class="secure-image"
      @loadeddata="handleMediaLoad"
      @error="handleMediaError"
      @click="handleClick"
      @contextmenu.prevent.stop
      @dragstart.prevent.stop
      @selectstart.prevent.stop
      draggable="false"
      oncontextmenu="return false"
      onselectstart="return false"
      ondragstart="return false"
      autoplay
      controlsList="nodownload noplaybackrate noremoteplayback"
      disablePictureInPicture
      disableRemotePlayback
      muted
      loop
      playsinline
    />
    <img
      v-else-if="secureImageUrl"
      ref="imageRef"
      :src="secureImageUrl"
      :alt="alt"
      :class="[imageClass, cursorClass]"
      class="secure-image"
      @load="handleMediaLoad"
      @error="handleMediaError"
      @click="handleClick"
      @contextmenu.prevent.stop
      @dragstart.prevent.stop
      @selectstart.prevent.stop
      draggable="false"
      oncontextmenu="return false"
      onselectstart="return false"
      ondragstart="return false"
    />

    <!-- Security overlay (invisible but prevents right-click) -->
    <div 
      v-if="secureImageUrl && !loading && !error"
      class="absolute inset-0 pointer-events-none select-none"
      style="z-index: 1;"
      @contextmenu.prevent.stop
    ></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { centralizedImageSecurity } from '@/services/centralizedImageSecurity';
import { isLikelyVideoUrl } from '@/utils/validation';

interface Props {
  imageUrl: string;
  wallpaperId: string;
  isPreview?: boolean;
  alt?: string;
  containerClass?: string;
  imageClass?: string;
  aspectClass?: string;
  clickable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isPreview: true,
  alt: 'Wallpaper preview',
  containerClass: 'relative',
  imageClass: 'w-full h-full object-cover',
  aspectClass: 'w-full h-full',
  clickable: false
});

const emit = defineEmits<{
  click: [];
  load: [];
  error: [Error];
}>();

// Refs
const containerRef = ref<HTMLElement>();
const imageRef = ref<HTMLImageElement>();
const videoRef = ref<HTMLVideoElement>();
const secureImageUrl = ref<string>('');
const loading = ref(true);
const error = ref(false);
const blobUrls = ref<string[]>([]);
const isVideoUrl = ref(false);

// Computed
const cursorClass = computed(() => {
  return props.clickable ? 'cursor-pointer' : 'cursor-default';
});

// Methods
const loadSecureImage = async () => {
  if (!props.imageUrl || !props.wallpaperId) {
    error.value = true;
    loading.value = false;
    return;
  }

  try {
    loading.value = true;
    error.value = false;
    isVideoUrl.value = isLikelyVideoUrl(props.imageUrl);

    const result = await centralizedImageSecurity.getSecureImageUrl({
      imageUrl: props.imageUrl,
      wallpaperId: props.wallpaperId,
      isPreview: true
    });

    secureImageUrl.value = result.secureUrl;
    
    // Track blob URLs for cleanup
    if (result.secureUrl.startsWith('blob:')) {
      blobUrls.value.push(result.secureUrl);
    }

  } catch (err) {
    console.error('[SecureWallpaperImage] Failed to load secure image:', err);
    error.value = true;
    emit('error', err instanceof Error ? err : new Error('Failed to load image'));
  } finally {
    loading.value = false;
  }
};

const handleMediaLoad = () => {
  loading.value = false;
  error.value = false;
  
  // Apply additional security protections to the image element
  if (imageRef.value) {
    centralizedImageSecurity.protectImageElement(imageRef.value);
  } else if (videoRef.value) {
    centralizedImageSecurity.protectVideoElement(videoRef.value);
  }
  
  emit('load');
};

const handleMediaError = () => {
  loading.value = false;
  error.value = true;
  emit('error', new Error('Image failed to load'));
};

const handleClick = () => {
  if (props.clickable) {
    emit('click');
  }
};

const cleanupBlobUrls = () => {
  blobUrls.value.forEach(url => {
    try {
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[SecureWallpaperImage] Failed to revoke blob URL:', err);
    }
  });
  blobUrls.value = [];
};

// Watchers
watch([() => props.imageUrl, () => props.wallpaperId], () => {
  cleanupBlobUrls();
  loadSecureImage();
}, { immediate: false });

// Lifecycle
onMounted(() => {
  loadSecureImage();
  
  // Add global keyboard event prevention
  const handleKeyDown = (e: KeyboardEvent) => {
    // Prevent common save shortcuts when focused on our component
    if (containerRef.value?.contains(document.activeElement)) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
  
  document.addEventListener('keydown', handleKeyDown);
  
  // Cleanup function
  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
    cleanupBlobUrls();
  });
});

onUnmounted(() => {
  cleanupBlobUrls();
});
</script>

<style scoped>
.secure-wallpaper-image {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.secure-image {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: auto;
}

/* Prevent text selection and dragging */
.secure-wallpaper-image * {
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
}

/* Additional security styles */
.secure-wallpaper-image::selection {
  background: transparent;
}

.secure-wallpaper-image::-moz-selection {
  background: transparent;
}
</style>